/**
 * Signed-out notice (2026-10-21). check.php answers a request from a lapsed session with a
 * `<script>self.location=…</script>` body that a fetch never runs, so the page used to go quietly blank or
 * report a permission error. The gate (ft_require_login) also sends `X-FT-Auth: login-required`; on a page
 * that loaded signed in, that header raises this notice. Presentation only: nothing is retried or blocked.
 */

const ART = 'https://cdn.fatetide.com/images/site/errors/401-hourglass-2-800.webp';
const AUTH_HEADER = 'X-FT-Auth';

function signedInAtLoad(): boolean {
    const boot = (window as Window & { FateTide?: { user?: { id?: unknown } } }).FateTide;
    return String(boot?.user?.id ?? '') !== '';
}

function showSignedOutNotice(): void {
    if (!signedInAtLoad() || document.getElementById('ft-signed-out')) return;
    const wrap = document.createElement('div');
    wrap.id = 'ft-signed-out';
    wrap.setAttribute('role', 'dialog');
    wrap.setAttribute('aria-modal', 'true');
    wrap.setAttribute('aria-labelledby', 'ft-signed-out-title');
    wrap.style.cssText = 'position:fixed;inset:0;z-index:2147483000;display:flex;align-items:center;justify-content:center;background:rgba(6,13,20,.8);padding:16px;';
    const box = document.createElement('div');
    box.style.cssText = 'max-width:440px;width:100%;overflow:hidden;background:#0d1720;color:#e8edf1;border-radius:12px;text-align:center;font:15px/1.5 system-ui,sans-serif;box-shadow:0 12px 44px rgba(0,0,0,.6);';
    const art = document.createElement('img');
    art.src = ART;
    art.alt = '';
    // whole hourglass in frame with headroom; the lower edge fades into the dialog
    art.style.cssText = 'display:block;width:100%;height:210px;object-fit:cover;object-position:64% 30%;'
        + '-webkit-mask-image:linear-gradient(#000 72%,transparent);mask-image:linear-gradient(#000 72%,transparent);';
    const body = document.createElement('div');
    body.style.cssText = 'padding:18px 20px 22px;';
    const title = document.createElement('div');
    title.id = 'ft-signed-out-title';
    title.textContent = 'Your thread has slipped';
    title.style.cssText = 'font:600 20px/1.3 Georgia,"Times New Roman",serif;margin-bottom:8px;';
    const text = document.createElement('div');
    text.textContent = 'Your session ended, so that last action did not go through. Sign in again (it opens in a new tab), then try it again here.';
    text.style.cssText = 'color:#a8b7c3;margin-bottom:16px;';
    const signIn = document.createElement('a');
    signIn.className = 'btn btn-warning me-2';
    // login.php brings the viewer back to this page afterwards (ft_login_return_path() vets it)
    signIn.href = `${window.location.origin}/login.php?return=${encodeURIComponent(window.location.pathname + window.location.search)}`;
    signIn.target = '_blank';
    signIn.rel = 'noopener';
    signIn.textContent = 'Sign in';
    const dismiss = document.createElement('button');
    dismiss.type = 'button';
    dismiss.className = 'btn btn-outline-light';
    dismiss.textContent = 'Close';
    dismiss.addEventListener('click', () => wrap.remove());
    body.append(title, text, signIn, dismiss);
    box.append(art, body);
    wrap.append(box);
    (document.body ?? document.documentElement).append(wrap);
    signIn.focus();
}

const pageFetch = window.fetch.bind(window);
window.fetch = (input: RequestInfo | URL, init?: RequestInit): Promise<Response> =>
    pageFetch(input, init).then((response) => {
        if (response.headers.get(AUTH_HEADER) === 'login-required') showSignedOutNotice();
        return response;
    });

if (typeof XMLHttpRequest !== 'undefined') {
    const send = XMLHttpRequest.prototype.send;
    const watched = new WeakSet<XMLHttpRequest>(); // one listener per instance, however often it is reused
    XMLHttpRequest.prototype.send = function (this: XMLHttpRequest, body?: Document | XMLHttpRequestBodyInit | null): void {
        if (!watched.has(this)) {
            watched.add(this);
            this.addEventListener('load', () => {
                if (this.getResponseHeader(AUTH_HEADER) === 'login-required') showSignedOutNotice();
            });
        }
        send.call(this, body);
    };
}
